import { useEffect, useId, useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError, request } from "../../lib/api";
import { useSession } from "../../app/session";
import { Icon } from "../../components/ui/Icon";
import { Button, Checkbox } from "../../components/ui/primitives";

interface FieldErrors {
  email?: string;
  password?: string;
  code?: string;
  newPassword?: string;
  confirmPassword?: string;
}

/** The single-use token a password reset link carries after '#reset=', which no server ever receives. */
function readResetToken(): string | null {
  const match = /^#reset=(.+)$/.exec(window.location.hash);
  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * Sign-in.
 *
 * The tenant is normally implied by the host name; on a plain localhost there is
 * no subdomain to read, so the field is offered rather than guessed. Failure is
 * reported exactly as the server words it, which never distinguishes a wrong
 * password from an unknown address.
 *
 * An account protected by an authenticator app is asked for a code after the
 * password, and no session exists until that code is accepted.
 *
 * A forgotten password is reset by email: the answer to the request never says
 * whether the address has an account, and the emailed link opens this screen to
 * choose a new password. Saving it ends every session and signs nobody in.
 */
export function LoginScreen() {
  const { signIn, completeMfaSignIn, signOut, status } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tenantSlug, setTenantSlug] = useState(() => {
    const host = window.location.hostname;
    const parts = host.split(".");

    // A real subdomain implies the tenant.
    if (parts.length > 2 && parts[0] !== "www") return parts[0];

    // On a plain localhost there is no subdomain to read, so a development build
    // may name its workspace up front. It stays editable, and the variable is
    // absent in any real deployment, where the host name answers the question.
    return import.meta.env.VITE_DEFAULT_TENANT ?? "";
  });

  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // A field's message is linked to its input while it shows, so a screen reader reads it with the field.
  const idPrefix = useId();
  const errorId = (field: keyof FieldErrors) => `${idPrefix}-${field}-error`;
  const describedBy = (field: keyof FieldErrors) => (fieldErrors[field] ? errorId(field) : undefined);

  // The second step, when the account has an authenticator app.
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);

  // Asking for a reset link, or choosing a new password with one.
  const [resetToken, setResetToken] = useState<string | null>(readResetToken);
  const [mode, setMode] = useState<"signIn" | "forgot" | "reset">(() =>
    readResetToken() ? "reset" : "signIn",
  );
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  // The token is kept in the page and taken out of the address bar, so it stays out of history.
  useEffect(() => {
    if (location.hash.startsWith("#reset=")) {
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: location.state },
      );
    }
  }, [location, navigate]);

  if (status === "signedIn" && mode !== "reset") {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? "/"} replace />;
  }

  const describe = (err: unknown) =>
    err instanceof ApiError ? err.message : "Could not reach the server. Check that the API is running.";

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    // Nothing is sent until both fields hold something. An empty form is a slip to point
    // out, not a failed sign-in for the server to count against the account.
    const trimmedEmail = email.trim();
    const problems: FieldErrors = {};

    if (!trimmedEmail) {
      problems.email = "Enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+$/.test(trimmedEmail)) {
      problems.email = "Enter your email address in the form name@company.com.";
    }

    if (!password) {
      problems.password = "Enter your password.";
    }

    setFieldErrors(problems);
    if (problems.email || problems.password) return;

    setBusy(true);

    try {
      const outcome = await signIn(trimmedEmail, password, tenantSlug.trim() || undefined, remember);

      if (outcome.kind === "mfaRequired") {
        setChallenge(outcome.challengeToken);
        setCode("");
        setUseRecoveryCode(false);
      }
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const entered = code.trim();

    if (!entered || !challenge) {
      setFieldErrors({
        code: useRecoveryCode
          ? "Enter one of your recovery codes."
          : "Enter the 6-digit code from your authenticator app.",
      });
      return;
    }

    setFieldErrors({});
    setBusy(true);

    try {
      await completeMfaSignIn(challenge, entered);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  const startAgain = () => {
    setChallenge(null);
    setCode("");
    setPassword("");
    setError(null);
    setFieldErrors({});
  };

  const switchTo = (next: "signIn" | "forgot") => {
    setMode(next);
    setResetToken(null);
    setError(null);
    setNotice(null);
    setFieldErrors({});
  };

  const requestReset = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    const trimmedEmail = email.trim();

    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+$/.test(trimmedEmail)) {
      setFieldErrors({
        email: trimmedEmail
          ? "Enter your email address in the form name@company.com."
          : "Enter your email address.",
      });
      return;
    }

    setFieldErrors({});
    setBusy(true);

    try {
      // The answer is the same whether or not the address has an account.
      const result = await request<{ message: string }>("/auth/password-reset", {
        method: "POST",
        body: { email: trimmedEmail, tenantSlug: tenantSlug.trim() || null },
        anonymous: true,
      });

      setNotice(result.message);
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  const completeReset = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    // The server applies the full policy; these are the slips worth catching first.
    const problems: FieldErrors = {};

    if (newPassword.length < 12) {
      problems.newPassword = "Use at least 12 characters, mixing upper case, lower case and a digit or symbol.";
    }

    if (confirmPassword !== newPassword) {
      problems.confirmPassword = "The two passwords do not match.";
    }

    setFieldErrors(problems);
    if (problems.newPassword || problems.confirmPassword || !resetToken) return;

    setBusy(true);

    try {
      await request("/auth/password-reset/complete", {
        method: "POST",
        body: { token: resetToken, newPassword },
        anonymous: true,
      });

      // The reset ended every session, this browser's included.
      if (status === "signedIn") await signOut();

      setNewPassword("");
      setConfirmPassword("");
      setPassword("");
      switchTo("signIn");
      setNotice("Your password has been changed. Sign in with the new one.");
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  };

  const alert = error && (
    <div
      role="alert"
      style={{
        background: "var(--red-wash)",
        border: "1px solid var(--red-edge)",
        borderRadius: "var(--r)",
        padding: "10px 12px",
        marginBottom: 16,
        font: "var(--t-small)",
        color: "#ff9ba1",
      }}
    >
      {error}
    </div>
  );

  const notification = notice && (
    <div
      role="status"
      style={{
        background: "var(--green-wash)",
        border: "1px solid rgba(34,197,94,0.35)",
        borderRadius: "var(--r)",
        padding: "10px 12px",
        marginBottom: 16,
        font: "var(--t-small)",
        color: "var(--text)",
      }}
    >
      {notice}
    </div>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 620px)",
        background: "var(--ink-900)",
      }}
    >
      <aside
        style={{
          position: "relative",
          display: "grid",
          placeItems: "center",
          padding: 48,
          overflow: "hidden",
          background:
            "radial-gradient(120% 90% at 30% 20%, rgba(224,30,43,0.14), transparent 58%), linear-gradient(160deg, #0b0e13, #05070a)",
        }}
      >
        <div style={{ textAlign: "center", position: "relative", zIndex: 1 }}>
          {/* The official logo, exactly as supplied. */}
          <img
            src="/brand/wolfray-ai-logo.png"
            alt="Wolfray AI"
            style={{ width: 200, height: "auto", marginBottom: 18 }}
          />
          <div
            style={{
              font: "800 42px/1 var(--font)",
              letterSpacing: "0.04em",
            }}
          >
            WOLFRAY <span style={{ color: "var(--red)" }}>AI</span>
          </div>
          <div
            style={{
              font: "500 24px/1.2 var(--font)",
              color: "var(--text-soft)",
              marginTop: 6,
            }}
          >
            EventOS
          </div>
          <p
            style={{
              font: "400 17px/1.5 var(--font)",
              color: "var(--text-soft)",
              marginTop: 20,
            }}
          >
            Events that move humanity forward.
          </p>
          <div
            style={{
              marginTop: 26,
              paddingTop: 22,
              borderTop: "1px solid var(--line)",
              font: "600 11px/1.6 var(--font)",
              letterSpacing: "0.24em",
              color: "var(--text-muted)",
            }}
          >
            PEOPLE / IDEAS / OPPORTUNITIES / A BRIGHTER TOMORROW
          </div>
        </div>
      </aside>

      <main style={{ display: "grid", placeItems: "center", padding: 32 }}>
        {challenge ? (
          <form
            onSubmit={verify}
            className="card"
            style={{ width: "min(440px, 100%)", padding: 32 }}
            noValidate
          >
            <h1 style={{ font: "700 30px/1.2 var(--font)", letterSpacing: "-0.02em" }}>
              Two-factor check
            </h1>
            <p className="soft" style={{ marginTop: 6, marginBottom: 22 }}>
              {useRecoveryCode
                ? "Enter one of the recovery codes you saved when you set up two-factor authentication. Each code works once."
                : "Open your authenticator app and enter the 6-digit code it shows for this account."}
            </p>

            {alert}

            <label className="field">
              <span className="field__label">{useRecoveryCode ? "Recovery code" : "Authentication code"}</span>
              <input
                className={`input mono${fieldErrors.code ? " input--invalid" : ""}`}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                autoComplete="one-time-code"
                inputMode={useRecoveryCode ? "text" : "numeric"}
                placeholder={useRecoveryCode ? "XXXX-XXXX-XXXX-XXXX" : "123456"}
                aria-invalid={fieldErrors.code ? true : undefined}
                aria-describedby={describedBy("code")}
                autoFocus
              />
              {fieldErrors.code && (
                <span id={errorId("code")} className="field__error">
                  {fieldErrors.code}
                </span>
              )}
            </label>

            <Button tone="primary" size="lg" block type="submit" busy={busy}>
              Verify and sign in
              {!busy && <Icon.arrowRight />}
            </Button>

            <div className="row row--between" style={{ marginTop: 16 }}>
              <Button
                size="sm"
                tone="ghost"
                type="button"
                onClick={() => {
                  setUseRecoveryCode((using) => !using);
                  setCode("");
                  setFieldErrors({});
                  setError(null);
                }}
              >
                {useRecoveryCode ? "Use the authenticator app" : "Use a recovery code"}
              </Button>
              <Button size="sm" tone="ghost" type="button" onClick={startAgain}>
                Start again
              </Button>
            </div>
          </form>
        ) : mode === "reset" ? (
          <form
            onSubmit={completeReset}
            className="card"
            style={{ width: "min(440px, 100%)", padding: 32 }}
            noValidate
          >
            <h1 style={{ font: "700 30px/1.2 var(--font)", letterSpacing: "-0.02em" }}>
              Choose a new password
            </h1>
            <p className="soft" style={{ marginTop: 6, marginBottom: 22 }}>
              Use at least 12 characters, mixing upper case, lower case and a digit or symbol. Saving
              it signs out every session that used the old one.
            </p>

            {alert}

            <label className="field">
              <span className="field__label">New password</span>
              <input
                className={`input${fieldErrors.newPassword ? " input--invalid" : ""}`}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={newPassword}
                aria-invalid={fieldErrors.newPassword ? true : undefined}
                aria-describedby={describedBy("newPassword")}
                onChange={(event) => {
                  setNewPassword(event.target.value);
                  if (fieldErrors.newPassword) setFieldErrors({ ...fieldErrors, newPassword: undefined });
                }}
                autoFocus
              />
              {fieldErrors.newPassword && (
                <span id={errorId("newPassword")} className="field__error">
                  {fieldErrors.newPassword}
                </span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Confirm the new password</span>
              <input
                className={`input${fieldErrors.confirmPassword ? " input--invalid" : ""}`}
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={confirmPassword}
                aria-invalid={fieldErrors.confirmPassword ? true : undefined}
                aria-describedby={describedBy("confirmPassword")}
                onChange={(event) => {
                  setConfirmPassword(event.target.value);
                  if (fieldErrors.confirmPassword) setFieldErrors({ ...fieldErrors, confirmPassword: undefined });
                }}
              />
              {fieldErrors.confirmPassword && (
                <span id={errorId("confirmPassword")} className="field__error">
                  {fieldErrors.confirmPassword}
                </span>
              )}
            </label>

            <div className="row row--between" style={{ margin: "4px 0 18px" }}>
              <Checkbox
                label="Show passwords"
                checked={showPassword}
                onChange={(event) => setShowPassword(event.target.checked)}
              />
            </div>

            <Button tone="primary" size="lg" block type="submit" busy={busy}>
              Save the new password
              {!busy && <Icon.arrowRight />}
            </Button>

            <div className="row row--between" style={{ marginTop: 16 }}>
              <Button size="sm" tone="ghost" type="button" onClick={() => switchTo("forgot")}>
                Ask for a new link
              </Button>
              <Button size="sm" tone="ghost" type="button" onClick={() => switchTo("signIn")}>
                Back to sign in
              </Button>
            </div>
          </form>
        ) : mode === "forgot" ? (
          <form
            onSubmit={requestReset}
            className="card"
            style={{ width: "min(440px, 100%)", padding: 32 }}
            noValidate
          >
            <h1 style={{ font: "700 30px/1.2 var(--font)", letterSpacing: "-0.02em" }}>
              Reset your password
            </h1>
            <p className="soft" style={{ marginTop: 6, marginBottom: 22 }}>
              Enter the email address you sign in with. If it belongs to an account, a link to
              choose a new password is sent to it.
            </p>

            {alert}
            {notification}

            <label className="field">
              <span className="field__label">Email address</span>
              <input
                className={`input${fieldErrors.email ? " input--invalid" : ""}`}
                type="email"
                autoComplete="username"
                value={email}
                aria-invalid={fieldErrors.email ? true : undefined}
                aria-describedby={describedBy("email")}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: undefined });
                }}
                placeholder="you@company.com"
                autoFocus
              />
              {fieldErrors.email && (
                <span id={errorId("email")} className="field__error">
                  {fieldErrors.email}
                </span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Organisation</span>
              <input
                className="input"
                value={tenantSlug}
                onChange={(event) => setTenantSlug(event.target.value)}
                placeholder="wolfray"
                autoComplete="organization"
              />
            </label>

            <Button tone="primary" size="lg" block type="submit" busy={busy}>
              Send the reset link
              {!busy && <Icon.arrowRight />}
            </Button>

            <div className="row row--between" style={{ marginTop: 16 }}>
              <Button size="sm" tone="ghost" type="button" onClick={() => switchTo("signIn")}>
                Back to sign in
              </Button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={submit}
            className="card"
            style={{ width: "min(440px, 100%)", padding: 32 }}
            noValidate
          >
            <h1 style={{ font: "700 30px/1.2 var(--font)", letterSpacing: "-0.02em" }}>
              Welcome back
            </h1>
            <p className="soft" style={{ marginTop: 6, marginBottom: 22 }}>
              Sign in to your Wolfray AI EventOS account.
            </p>

            {alert}
            {notification}

            <label className="field">
              <span className="field__label">Email address</span>
              <div style={{ position: "relative" }}>
                <Icon.mail
                  width={16}
                  height={16}
                  style={{
                    position: "absolute",
                    insetInlineStart: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-muted)",
                  }}
                />
                <input
                  className={`input${fieldErrors.email ? " input--invalid" : ""}`}
                  style={{ paddingInlineStart: 36 }}
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  aria-invalid={fieldErrors.email ? true : undefined}
                  aria-describedby={describedBy("email")}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (fieldErrors.email) setFieldErrors({ ...fieldErrors, email: undefined });
                  }}
                  placeholder="you@company.com"
                />
              </div>
              {fieldErrors.email && (
                <span id={errorId("email")} className="field__error">
                  {fieldErrors.email}
                </span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Password</span>
              <div style={{ position: "relative" }}>
                <Icon.lock
                  width={16}
                  height={16}
                  style={{
                    position: "absolute",
                    insetInlineStart: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "var(--text-muted)",
                  }}
                />
                <input
                  className={`input${fieldErrors.password ? " input--invalid" : ""}`}
                  style={{ paddingInline: "36px" }}
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  value={password}
                  aria-invalid={fieldErrors.password ? true : undefined}
                  aria-describedby={describedBy("password")}
                  onChange={(event) => {
                    setPassword(event.target.value);
                    if (fieldErrors.password) setFieldErrors({ ...fieldErrors, password: undefined });
                  }}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((shown) => !shown)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    insetInlineEnd: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    padding: 2,
                  }}
                >
                  {showPassword ? (
                    <Icon.eyeOff width={17} height={17} />
                  ) : (
                    <Icon.eye width={17} height={17} />
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <span id={errorId("password")} className="field__error">
                  {fieldErrors.password}
                </span>
              )}
            </label>

            <label className="field">
              <span className="field__label">Organisation</span>
              <input
                className="input"
                value={tenantSlug}
                onChange={(event) => setTenantSlug(event.target.value)}
                placeholder="wolfray"
                autoComplete="organization"
              />
              <span className="field__hint">
                Your workspace name. On a custom domain this is filled in for you.
              </span>
            </label>

            <div className="row row--between" style={{ margin: "4px 0 18px" }}>
              <Checkbox
                label="Remember me"
                checked={remember}
                onChange={(event) => setRemember(event.target.checked)}
              />
              <Button size="sm" tone="ghost" type="button" onClick={() => switchTo("forgot")}>
                Forgot password?
              </Button>
            </div>

            <Button tone="primary" size="lg" block type="submit" busy={busy}>
              Sign in
              {!busy && <Icon.arrowRight />}
            </Button>

            <div
              className="row"
              style={{
                gap: 9,
                marginTop: 22,
                paddingTop: 18,
                borderTop: "1px solid var(--line)",
              }}
            >
              <Icon.shield
                width={20}
                height={20}
                style={{ color: "var(--text-muted)", flex: "none" }}
              />
              <div>
                <b style={{ display: "block", font: "600 12.5px/1.4 var(--font)" }}>
                  Trusted by event leaders worldwide
                </b>
                <span className="muted" style={{ font: "var(--t-small)" }}>
                  Secure. Compliant. Built for what&rsquo;s next.
                </span>
              </div>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}
